import { Invoice } from '../../types';
import api, { pagedResult, InvoiceListQuery, PagedResult } from './client';

export const invoicesService = {
  getAllInvoices: async (query?: InvoiceListQuery): Promise<Invoice[]> => {
    const response = await api.get<Invoice[]>('/invoices', { params: query });
    return response.data;
  },
  /** Same endpoint, but keeps the unpaged total for a paging control. */
  getInvoicesPage: async (query: InvoiceListQuery): Promise<PagedResult<Invoice>> => {
    const response = await api.get<Invoice[]>('/invoices', { params: query });
    return pagedResult(response);
  },
  getInvoice: async (id: string): Promise<Invoice> => {
    const response = await api.get<Invoice>(`/invoices/${id}`);
    return response.data;
  },
  updateInvoice: async (id: string, payload: { notes?: string }): Promise<Invoice> => {
    const response = await api.patch<Invoice>(`/invoices/${id}`, payload);
    return response.data;
  },
  // Totals are derived server-side from these lines — the client never sends an amount.
  replaceInvoiceItems: async (
    id: string,
    items: {
      description: string;
      quantity: number;
      unitPrice: number;
      taxTypeId?: string | null;
      partId?: string | null;
    }[],
  ): Promise<Invoice> => {
    const response = await api.put<Invoice>(`/invoices/${id}/items`, { items });
    return response.data;
  },
  /**
   * The same PDF the customer receives attached to the invoice email.
   *
   * Returned as a blob plus the filename the API suggests, so the caller can
   * save it; the download itself is the page's job, not this layer's.
   */
  downloadInvoicePdf: async (
    id: string,
  ): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.get(`/invoices/${id}/pdf`, {
      responseType: 'blob',
    });

    const disposition = String(response.headers?.['content-disposition'] ?? '');
    const match = disposition.match(/filename="?([^"]+)"?/);

    return {
      blob: response.data as Blob,
      filename: match?.[1] ?? `invoice-${id}.pdf`,
    };
  },
  sendInvoice: async (id: string): Promise<Invoice> => {
    const response = await api.post<Invoice>(`/invoices/${id}/send`);
    return response.data;
  },
  deleteInvoice: async (id: string): Promise<void> => {
    await api.delete(`/invoices/${id}`);
  },
  generateInvoice: async (appointmentId: string): Promise<Invoice> => {
    const response = await api.post<Invoice>('/invoices/generate', { appointmentId });
    return response.data;
  },
};
